import React, { useState } from 'react';
import { Card } from '@components/atoms/Card';
import { Button } from '@components/atoms/Button';
import { TextareaInput } from '@components/atoms/TextareaInput';
import { X } from 'lucide-react';
import { cn } from '@utils/cn';

interface RejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
  className?: string;
}

export const RejectionModal: React.FC<RejectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  className = '',
}) => {
  const [reason, setReason] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    onConfirm(reason || 'No note provided');
    setReason('');
  };

  const handleClose = () => {
    setReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <Card className={cn('max-w-md w-full', className)}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Reject Task
          </h3>
          <Button
            variant="tertiary"
            size="md"
            onlyIcon
            onClick={handleClose}
            leftIcon={<X className="w-4 h-4" />}
            className="text-gray-500 hover:text-gray-700"
          />
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Are you sure you want to reject this task? Please provide a reason for rejection.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rejection Note
          </label>
          <TextareaInput
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            rows={3}
            className="w-full"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            Reject Task
          </Button>
        </div>
      </Card>
    </div>
  );
};